import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";

// Actions
import {
  selectLocation,
  restoreLocation,
  removeLocation,
  errorResponse,
  toggleRemoveLocationMode,
  setMainViewSection,
  calculateTotalSamplesNeeded,
} from "../../actions";

// List Components
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";

// Icons
import GpsNotFixedIcon from "@material-ui/icons/GpsNotFixed";
import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import DeleteIcon from "@material-ui/icons/Delete";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import UndoIcon from "@material-ui/icons/Undo";
import AlertIcon from "@material-ui/icons/Warning";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";

// Other
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import { percentage } from "../../constants/utilityFunctions";
import { decimalPoints } from "../../constants/constants";

const Location = (props) => {
  const handleClick = (e, id) => {
    if (!props.acquiring) {
      // disable switching locations while web camera is active
      props.dispatch(selectLocation(id, props.currentAcqType));
      if (props.selected) {
        props.dispatch(setMainViewSection("sampleFocused"));
      }
    }
  };

  const handleRemoveOrRestoreLocation = (id) => {
    if (props.removed) {
      //Restore removed location

      props.dispatch(restoreLocation(id));
      props.dispatch(calculateTotalSamplesNeeded());
    } else if (!props.removed) {
      //Remove location
      if (props.samplesExcludedDiff === 0) {
        // Select next location if the slected location is being removed
        if (props.selected) {
          props.dispatch(selectLocation(props.nextLocId, props.currentAcqType));
        }
        props.dispatch(removeLocation(id));
        props.dispatch(calculateTotalSamplesNeeded());
      } else {
        props.dispatch(
          errorResponse(
            `${props.name} has samples included in study. Exclude samples to remove location.`
          )
        );
      }
    }
    props.dispatch(toggleRemoveLocationMode());
  };

  const IconLeft = () => {
    const iconLeftStyles = makeStyles({
      icon: customTheme().iconMedium,
    });

    const classes = iconLeftStyles();
    if (props.removeLocationMode) {
      return null;
    } else if (props.alert) {
      return <AlertIcon className={classes.icon} color="error" />;
    } else if (props.completed) {
      // Complete Icon
      return <CheckBoxIcon className={classes.icon} color="primary" />;
    } else if (props.selected) {
      // Selected Icon
      return <GpsFixedIcon className={classes.icon} />;
    } else {
      // Not selected Icon
      return <GpsNotFixedIcon className={classes.icon} />;
    }
  };

  const IconSecondary = () => {
    const IconSecondaryStyles = makeStyles({
      icon: customTheme().iconMedium,
    });

    const classes = IconSecondaryStyles();
    if (props.removed) {
      return <UndoIcon className={classes.icon} />;
    } else if (props.samplesExcludedDiff !== 0) {
      return <DeleteForeverIcon className={classes.icon} />;
    } else {
      return <DeleteIcon className={classes.icon} />;
    }
  };

  const InfoRight = () => {
    const infoRightStyles = makeStyles({
      numberSws: {
        color: colors[props.theme].primary,
        display: "inline-block",
        marginBottom: 0,
        marginTop: 0,
        marginLeft: "10px",
        fontSize: "1.2rem",
        fontWeight: 600,
      },
      numberThickness: {
        color: colors[props.theme].secondary,
        display: "inline-block",
        marginBottom: 0,
        marginTop: 0,
        marginLeft: "10px",
        fontSize: "1.2rem",
        fontWeight: 600,
      },
      units: {
        fontSize: "1.2rem",
      },
      numberContainer: {
        width: "185px",
        display: "flex",
        justifyContent: "space-between",
      },
    });

    const classes = infoRightStyles();
    return (
      <div className={classes.numberContainer}>
        <h1 className={classes.numberSws}>
          {props.shearWaveSpeed === null
            ? ""
            : props.shearWaveSpeed.toFixed(decimalPoints)}
          {props.shearWaveSpeed !== null && (
            <span className={classes.units}> m/s</span>
          )}
        </h1>

        <h1 className={classes.numberThickness}>
          {props.thickness === null
            ? ""
            : props.thickness.toFixed(decimalPoints)}
          {props.thickness !== null && (
            <span className={classes.units}> mm </span>
          )}
        </h1>
      </div>
    );
  };

  const styles = makeStyles({
    root: {
      paddingRight: "28px",
      paddingLeft: "23px",
      height: "51px",
      "&.Mui-selected": {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      },
      "&.MuiListItem-button:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.16)",
      },
    },
    locationName: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      width: "40%",
      marginTop: "0px",
      marginBottom: "0px",
      textDecoration: props.removed && "line-through",
      textOverflow: "ellipsis",
      "& span": {
        fontSize: "1.2rem",
      },
    },
    icon: customTheme().iconMedium,
    progressBar: {
      backgroundImage: "none",
      backgroundColor: colors.progressBarBkgLocation,
      height: "30px",
      borderRadius: "4px",
      "& .MuiLinearProgress-barColorPrimary": {
        backgroundColor: colors[props.theme].primaryProgressBarAcquired,
      },
      "& .MuiLinearProgress-dashed": {
        display: "none",
      },
      "& .MuiLinearProgress-dashedColorPrimary": {
        backgroundImage: "unset",
      },
      "& .MuiLinearProgress-colorPrimary": {
        backgroundColor: colors[props.theme].primaryProgressBarAcquired,
      },
    },
    progressLabel: {
      position: "absolute",
      marginLeft: "5px",
      zIndex: 2,
      textAlign: "center",
      alignItems: "center",
      "& span": {
        width: "100%",
      },
    },
    secondaryAction: {
      right: "unset",
      marginLeft: "unset",
      paddingLeft: "11px",
    },
    returnAction: {
      right: "0px",
      position: "absolute",
      paddingRight: "0px",
      "& .MuiSvgIcon-root": {
        ...customTheme().iconMedium,
      },
    },
  });

  const classes = styles();

  return (
    <ListItem
      button
      selected={props.selected}
      className={classes.root}
      disabled={props.removed}
      onClick={(e) => handleClick(e, props.id)}
      style={{
        width: "100%",
        boxShadow: props.focus
          ? `inset 0px -2px 0px 0px ${colors[props.theme].primary}`
          : "0px 0px 0px 0px", // underline the list element if its the focus element
      }}
    >
      {/* STATUS ICON FOR THE LOCATION */}
      <ListItemIcon>
        <IconLeft />
      </ListItemIcon>

      <ListItemText className={classes.locationName}>
        <div className={classes.progressLabel}>
          <span>{props.name}</span>
        </div>
        <LinearProgress
          variant="buffer"
          value={props.processedProgress}
          valueBuffer={props.acquiredProgress}
          className={classes.progressBar}
        />
      </ListItemText>

      {props.leftView && !props.noSamples && <InfoRight />}

      {props.removeLocationMode && (
        <ListItemSecondaryAction className={classes.secondaryAction}>
          <IconButton onClick={() => handleRemoveOrRestoreLocation(props.id)}>
            <IconSecondary />
          </IconButton>
        </ListItemSecondaryAction>
      )}
      {props.leftView && props.selected && (
        <IconButton
          className={classes.returnAction}
          disableRipple
          onClick={() => props.dispatch(setMainViewSection("sampleFocused"))}
        >
          <KeyboardArrowRight />
        </IconButton>
      )}
    </ListItem>
  );
};

const mapStateToProps = (state, ownProps) => {
  const location = state.locations.byId[ownProps.id];
  const acqTypes = state.protocol.acq_types;

  let totalSamplesAcquired = 0;
  let totalSamplesProcessed = 0;
  let totalSamplesNeeded = 0;
  let totalSamplesExcluded = 0;

  //Calculate total samples processed
  for (let type of Object.keys(acqTypes)) {
    const samplesAcquired = location.samplesAcquired[type];
    const samplesProcessed = location.samplesProcessed[type];
    const samplesNeeded = location.samplesNeeded[type];

    totalSamplesExcluded += location.samplesExcluded[type];

    totalSamplesNeeded += samplesNeeded;
    if (samplesAcquired < samplesNeeded) {
      totalSamplesAcquired += samplesAcquired;
    } else {
      totalSamplesAcquired += samplesNeeded;
    }

    if (samplesProcessed < samplesNeeded) {
      totalSamplesProcessed += samplesProcessed;
    } else {
      totalSamplesProcessed += samplesNeeded;
    }
  }

  const samplesExcludedDiff = location.samples.length - totalSamplesExcluded;

  let filteredLocationAllIds = state.locations.allIds.filter(function (value) {
    return !state.protocol.locations[value].removed;
  });
  let selectedInd = filteredLocationAllIds.indexOf(
    state.locations.selectedLocationId
  );
  let nextLocId =
    filteredLocationAllIds.length === selectedInd + 1
      ? filteredLocationAllIds[0]
      : filteredLocationAllIds[selectedInd + 1];

  return {
    // just the data we need - nothing we dont
    // bc any changes to props force a rerender
    name: location.name,
    acquiring: state.device.acquiring,
    acquiredProgress: percentage(totalSamplesAcquired, totalSamplesNeeded),
    processedProgress: percentage(totalSamplesProcessed, totalSamplesNeeded),
    completed: location.completed,
    shearWaveSpeed: location.shearWaveSpeed.value,
    thickness: location.thickness.value,
    alert:
      location.alert.status && location.alert.sampleIncludedInStudy
        ? true
        : false,
    focus: state.utility.focusElement === location.id, // boolean - should element be underlined (teal)
    currentAcqType: state.locations.currentAcqType,
    selected: state.locations.selectedLocationId === location.id, // bool
    removeLocationMode: state.utility.mode.removeLocationMode,
    removed: state.protocol.locations[ownProps.id].removed,
    samplesExcludedDiff: samplesExcludedDiff,
    nextLocId: nextLocId,
    leftView: state.view.mainViewSection.orientation === "LEFT",
    noSamples:
      state.samples.idByType["CSI"].length === 0 &&
      state.samples.idByType["BMODE"].length === 0,
    theme: state.utility.theme,
  };
};

// conditionally skip render
function areEqual(prevProps, nextProps) {
  // prevent rerender when acquiring changes
  if (prevProps.acquiring !== nextProps.acquiring) {
    return true; // skip render
  } else {
    return false;
  }
}

export default connect(mapStateToProps)(React.memo(Location, areEqual));
